// ABOUTME: Interview detail page — grouped header with re-analyze/delete actions,
// ABOUTME: Q&A / raw transcript / evidence tabs, live job status (W2 P2)

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Info, Pencil, RotateCw, Trash2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Textarea } from '@/components/ui/textarea'
import { EditInterviewDialog } from '@/components/interviews/EditInterviewDialog'
import { patchInterview } from '@/api/interviews'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { TranscriptView } from '@/components/interviews/TranscriptView'
import { StatusBadge, deriveStatus } from '@/components/interviews/StatusBadge'
import {
  activeJobFor, latestJobFor, useDeleteInterview, useInterviewDetail, useJobs,
  useReanalyzeInterview,
} from '@/hooks/useInterviews'
import { useAuth } from '@/contexts/AuthContext'
import { hypothesisCode, hypothesisPath } from '@/lib/hypothesisIds'

function errText(err: any): string {
  const d = err?.response?.data?.detail
  return typeof d === 'string' ? d : (err?.message || 'Request failed')
}

export function InterviewDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const { data, isLoading, isError, error, refetch } = useInterviewDetail(id)
  const { data: jobs } = useJobs()
  const reanalyze = useReanalyzeInterview()
  const deleteInterview = useDeleteInterview()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editingTranscript, setEditingTranscript] = useState(false)
  const [transcriptDraft, setTranscriptDraft] = useState('')
  const [savingTranscript, setSavingTranscript] = useState(false)
  const queryClient = useQueryClient()

  const saveTranscript = async () => {
    if (!data) return
    setSavingTranscript(true)
    setActionError(null)
    try {
      await patchInterview(data.interview_id, { transcript: transcriptDraft })
      setEditingTranscript(false)
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['interview', data.interview_id] })
    } catch (e: any) {
      setActionError(e?.response?.data?.detail ?? 'Save failed')
    } finally {
      setSavingTranscript(false)
    }
  }

  const activeJob = id ? activeJobFor(jobs, id) : undefined
  const latestJob = id ? latestJobFor(jobs, id) : undefined

  const notFound = (error as any)?.response?.status === 404

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Link
        to="/dashboard/interviews"
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-alx-blue mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Interviews
      </Link>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-96" />
          <Skeleton className="h-4 w-72" />
          <Skeleton className="h-4 w-80" />
          <Skeleton className="h-64 w-full mt-6" />
        </div>
      ) : notFound ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-10 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Interview not found</h2>
          <p className="text-sm text-gray-600 mb-4">It may have been deleted.</p>
          <Link to="/dashboard/interviews">
            <Button variant="outline">Back to Interviews</Button>
          </Link>
        </div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">Couldn't load this interview.</p>
          <Button variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <div>
          {/* Screen-reader announcements for job state changes */}
          <div aria-live="polite" className="sr-only">
            {activeJob ? 'Re-analysis in progress' : latestJob?.status === 'failed' ? 'Last job failed' : ''}
          </div>

          {/* Header: identity / provenance / analysis state */}
          <div className="mb-6 space-y-1.5">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {data.interviewee}
                <span className="text-alx-blue font-medium"> — {data.group ?? 'Unassigned group'}</span>
                {data.institution && <span className="text-gray-500 font-normal"> · {data.institution}</span>}
              </h1>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)} disabled={!!activeJob}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit details
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={!!activeJob || reanalyze.isPending}
                  onClick={() => {
                    setActionError(null)
                    reanalyze.mutate(data.interview_id, { onError: (e) => setActionError(errText(e)) })
                  }}
                >
                  <RotateCw className="w-4 h-4 mr-1" />
                  {activeJob ? 'Re-analyzing…' : 'Re-analyze'}
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost" size="icon"
                    aria-label="Delete this interview"
                    disabled={!!activeJob}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {data.name && <span className="italic">"{data.name}"</span>}
              {data.date && <span> · {data.date}</span>}
              {data.facilitator && <span> · facilitated by {data.facilitator}</span>}
              {data.recording_link && (
                <>
                  {' · '}
                  <a href={data.recording_link} target="_blank" rel="noopener noreferrer" className="text-alx-blue hover:underline">
                    recording
                  </a>
                </>
              )}
            </p>
            <p className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
              <StatusBadge
                status={deriveStatus(data, activeJob, latestJob)}
                error={latestJob?.status === 'failed' ? latestJob.error : null}
              />
              <span>{data.question_count} questions</span>
              <span>· {data.evidence_count} evidence</span>
              {data.quality_score != null && (
                <span className="inline-flex items-center gap-1">
                  · quality {data.quality_score}/10
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button aria-label="What does the quality score mean?" className="text-gray-400 hover:text-gray-600">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        LLM quality-control score for extraction faithfulness. Below 7, re-read the
                        Q&amp;A tab against the raw transcript.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              )}
            </p>
            {actionError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center justify-between">
                {actionError}
                <button className="underline ml-3" onClick={() => setActionError(null)}>Dismiss</button>
              </div>
            )}
            {!activeJob && latestJob?.status === 'failed' && latestJob.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-wrap">
                Last {latestJob.kind} failed: {latestJob.error}
              </div>
            )}
          </div>

          <Tabs defaultValue="qa">
            <TabsList>
              <TabsTrigger value="qa">Q&amp;A</TabsTrigger>
              <TabsTrigger value="raw">Raw transcript</TabsTrigger>
              <TabsTrigger value="evidence">Evidence ({data.evidence_count})</TabsTrigger>
            </TabsList>

            <TabsContent value="qa" className="mt-4">
              {data.qa.length > 0 ? (
                <TranscriptView qa={data.qa} />
              ) : (
                <EmptyPane text="No structured Q&A recorded for this interview — this can follow an interrupted run. Re-analysis will rebuild it." />
              )}
            </TabsContent>

            <TabsContent value="raw" className="mt-4">
              {editingTranscript ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-alx-gold bg-alx-gold/10 px-3 py-2 text-sm text-alx-blue">
                    Saving replaces the stored transcript and queues a re-analysis — all Q&A and
                    evidence for this interview will be rebuilt from the corrected text.
                  </div>
                  <Textarea
                    value={transcriptDraft}
                    onChange={(e) => setTranscriptDraft(e.target.value)}
                    rows={18}
                    className="font-normal text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <Button onClick={saveTranscript} disabled={savingTranscript || !transcriptDraft.trim()}>
                      {savingTranscript ? 'Saving…' : 'Save & re-analyze'}
                    </Button>
                    <Button variant="outline" onClick={() => setEditingTranscript(false)}>Cancel</Button>
                  </div>
                </div>
              ) : data.raw_transcript ? (
                <div className="rounded-lg border border-gray-200 bg-white p-6">
                  <div className="flex justify-end mb-2">
                    <Button
                      variant="ghost" size="sm" disabled={!!activeJob}
                      onClick={() => { setTranscriptDraft(data.raw_transcript ?? ''); setEditingTranscript(true) }}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Edit transcript
                    </Button>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed max-w-[70ch]">
                    {data.raw_transcript}
                  </p>
                </div>
              ) : (
                <EmptyPane text="No raw transcript stored for this interview." />
              )}
            </TabsContent>

            <TabsContent value="evidence" className="mt-4">
              {data.evidence_by_hypothesis.length > 0 ? (
                <div className="space-y-6">
                  {data.evidence_by_hypothesis.map((grp) => (
                    <div key={grp.hypothesis_id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                      <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                        <Link
                          to={hypothesisPath(grp.hypothesis_id)}
                          className="font-medium text-alx-blue hover:underline"
                        >
                          {hypothesisCode(grp.hypothesis_id)}
                        </Link>
                        <p className="text-sm text-gray-700 mt-0.5">{grp.statement}</p>
                      </div>
                      <div className="p-5 space-y-3">
                        {grp.items.map((item) => (
                          <div key={item.evidence_id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            {item.key_quote && (
                              <p className="text-sm text-gray-800 italic mb-2">"{item.key_quote}"</p>
                            )}
                            <div className="flex gap-3 text-xs text-gray-600">
                              {item.evidence_type && <span>Type: <span className="font-medium">{item.evidence_type}</span></span>}
                              {item.strength && <span>Strength: <span className="font-medium">{item.strength}</span></span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyPane text="No evidence recorded — the analysis produced none, or a run was interrupted. Re-analysis will rebuild it." />
              )}
            </TabsContent>
          </Tabs>

          <EditInterviewDialog interview={data} open={editOpen} onClose={() => setEditOpen(false)} />

          {/* Delete confirm */}
          <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete {data.interviewee}'s interview?</DialogTitle>
                <DialogDescription>
                  This removes {data.evidence_count} evidence item{data.evidence_count === 1 ? '' : 's'} and{' '}
                  {data.question_count} Q&A pairs from the graph and cannot be undone.
                  You can re-ingest it later from the original transcript.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={deleteInterview.isPending}
                  onClick={() => deleteInterview.mutate(data.interview_id, {
                    onSuccess: () => navigate('/dashboard/interviews'),
                    onError: (e) => { setActionError(errText(e)); setConfirmDelete(false) },
                  })}
                >
                  {deleteInterview.isPending ? 'Deleting…' : 'Delete interview'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  )
}

function EmptyPane({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
      <p className="text-sm text-gray-600">{text}</p>
    </div>
  )
}
